import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  ConsumableItemType,
  PackingItemType,
  RemovableItemResponseType,
} from 'features/Order/services/packers'
import { withLoadingReducer } from 'utils/reducerHandler'
import { fetchPackingDetail, fetchPackingDetailRemovable } from './thunk'

export type PopupPackingDetailInitialStateType = {
  order: {
    order_id: string
    type: string
    invoice: string
    status: string
    kitchen_status: string
  }
  timestamp: {
    payment_verified_at: number
    waiting_confirmation_at: number
  }
  items: PackingItemType[]
  consumables: ConsumableItemType[]
  removableProducts: PackingItemType[]
  misc: {
    total_relabeled_item: number
    note: string
    gift: {
      sender_name: string
      receiver_name: string
      note: string
    }
  }
  selectedItemRowIndex: number | null
  selectedItem: PackingItemType | null
  canToggle: number[]
  fulfillmentId: string
  isWithoutPackaging: boolean
  tempNotes: { id: number; note: string; note_id: string; status_id: number }[]
}

const initialState: PopupPackingDetailInitialStateType = {
  order: {
    order_id: '',
    type: '',
    invoice: '',
    status: '',
    kitchen_status: '',
  },
  timestamp: {
    payment_verified_at: 0,
    waiting_confirmation_at: 0,
  },
  items: [],
  consumables: [],
  removableProducts: [],
  misc: {
    total_relabeled_item: 0,
    note: '',
    gift: {
      sender_name: '',
      receiver_name: '',
      note: '',
    },
  },
  selectedItemRowIndex: null,
  selectedItem: null,
  canToggle: [],
  fulfillmentId: '',
  isWithoutPackaging: false,
  tempNotes: [],
}

export const parseRemovableProductsToPackingItemType = (
  items: RemovableItemResponseType[] = [],
): PackingItemType[] =>
  items.map((item) => ({
    id: Number(item.item_id),
    monolith_id: '',
    type: item.type,
    kitchen_item_id: Number(item.item_id),
    special_type: item.type,
    product: {
      id: String(item.product_id),
      name: item.product_name,
      sku: item.sku,
      brand_name: '',
      image_url: item.product_image,
      bundle_product_id: '',
      has_kitchen: item.type === 'ASTRO_KITCHEN',
    },
    inventory: {
      rack: item.rack_name,
      order_qty: Number(item.quantity),
      scanned_qty: 0,
      note_id: '',
      note: '',
      status_id: 0,
    },
    details: [{ id: String(item.item_id), qty: Number(item.quantity) }],
    modifiers: [],
    add_ons: [],
    halal_info: item.halal_info,
    misc: { is_manual_input_sku: false, is_need_to_relabel: false },
  }))

const PopupPackingDetailSlice = createSlice({
  name: 'popupPackingDetail',
  initialState,
  reducers: {
    setPackingDetail: (state, action) => {
      state.order = action.payload.order
      state.timestamp = action.payload.timestamp
      state.items = action.payload.items
      state.consumables = action.payload.consumables
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiGift = (action.payload.misc?.gift || {}) as any
      state.misc = {
        ...action.payload.misc,
        gift: {
          sender_name: apiGift.sender_name || '',
          receiver_name: apiGift.receiver_name || '',
          note: apiGift.note || '',
        },
      }
    },
    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload
      state.selectedItemRowIndex = null
    },
    setSelectedItemRow: (state, action: PayloadAction<number>) => {
      state.selectedItemRowIndex = action.payload
      state.selectedItem = state.items[action.payload]
    },
    setItems: (state, action) => {
      state.items = action.payload
    },
    setTemporaryNote: (state, action) => {
      const copyStateItem = [...state.items]
      copyStateItem.forEach((item) => {
        const copyItem = { ...item }
        if (item.id === action.payload.id) {
          copyItem.inventory.note = action.payload.note
          copyItem.inventory.note_id = action.payload.note_id
          copyItem.inventory.status_id = action.payload.status_id
        }
      })
      state.items = copyStateItem
      state.tempNotes = [...state.tempNotes, action.payload]
    },
    setIsWIthoutPackaging: (state, action) => {
      state.isWithoutPackaging = action.payload
    },
    setRemovableProducts: (state, action) => {
      state.removableProducts = action.payload
    },
    resetState: () => initialState,
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<PopupPackingDetailInitialStateType>) => {
      builder.addCase(fetchPackingDetail.fulfilled, (state, action) => {
        if (action.payload) {
          state.order = action.payload.order
          state.timestamp = action.payload.timestamp

          state.consumables = action.payload.consumables
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const apiGift = (action.payload.misc?.gift || {}) as any
          state.misc = {
            ...action.payload.misc,
            gift: {
              sender_name: apiGift.sender_name || '',
              receiver_name: apiGift.receiver_name || '',
              note: apiGift.note || '',
            },
          }

          state.canToggle = action.payload.items
            .filter(
              (item: { misc: { is_manual_input_sku: boolean } }) => item.misc.is_manual_input_sku,
            )
            .map((item: { id: number }) => item.id)
          state.fulfillmentId = action.payload.fulfillment_id

          // map notes temporary to fit new PackingItemType
          state.items = action.payload.items.map((item) => {
            const tempNote = state.tempNotes.find((tempNote) => tempNote.id === item.id)
            return {
              ...item,
              inventory: {
                ...item.inventory,
                note: tempNote?.note,
                note_id: tempNote?.note_id,
                status_id: tempNote?.status_id,
              },
            }
          })
        }
      })
      builder.addCase(fetchPackingDetailRemovable.fulfilled, (state, action) => {
        if (action.payload) {
          state.removableProducts = parseRemovableProductsToPackingItemType(
            action.payload.data.removable_items,
          )
        }
      })
    },
    [fetchPackingDetail, fetchPackingDetailRemovable],
  ),
})

export const {
  setPackingDetail,
  setSelectedItem,
  setSelectedItemRow,
  resetState,
  setItems,
  setTemporaryNote,
  setIsWIthoutPackaging,
  setRemovableProducts,
} = PopupPackingDetailSlice.actions
export default PopupPackingDetailSlice.reducer
